import {
  DetailWareInfoListVo,
  LocalInfoDTO,
  OrderStatusVo,
  TagContentVo,
} from '@/apis/order/order.interface';
import { useTranslation } from '@/i18n/client';
import cn from 'clsx';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';

// 组件
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price/index';
import OutOfStackController from '@/utils/outofStock/outofStockController';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import AgeTipsModal from '../ageTipsModal';
import ApplySuccessModal from '../applySuccessModal';
import CommonButton from '../commonButton';
import OrderServiceAgreement from '../orderServiceAgreement';
import ProductTag from '../productTag';
import ReturnProductModal from '../returnProductModal';
import ReturnRequestModal from '../returnRequestModal';
// 图标
import TipIcon from '@/assets/icons/TipIcon.svg';
// 埋点
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25344';
// utils
import { ITEM_TYPE } from '@/components/product/const';
import { addCartUtil } from '@/utils/cartUtils';
import cookieUtils from '@/utils/cookieUtils';
import { jumpToDp } from '../../utils/jumpMethod';

interface orderDetailProductProps {
  orderInfo: {
    orderStatusDTO: OrderStatusVo;
    wareInfoDTOList: DetailWareInfoListVo[];
    orderId: string;
    postCode: string;
    orderTagContentVO?: TagContentVo;
    localInfo?: LocalInfoDTO;
  };
  onRefresh?: () => void;
}

const orderDetailProduct: React.FC<orderDetailProductProps> = observer(
  ({ orderInfo, onRefresh }) => {
    const { t } = useTranslation();
    const { orderStatusDTO, wareInfoDTOList, orderId, postCode, orderTagContentVO, localInfo } =
      orderInfo || {};
    const { orderStatusId, vipOrder } = orderStatusDTO || {};

    const langCookie = cookieUtils.getCookie('_lang');

    // 埋点
    const commonTrackParams = {
      orderId: String(orderId),
      orderStatus: orderStatusId as '1' | '3' | '8' | '6' | '5',
    };

    const navigate = useNavigate();

    const [curSkuId, setCurSkuId] = useState('');

    const [showReturnProductModal, setShowReturnProductModal] = useState(false);
    const [showReturnRequestModal, setShowReturnRequestModal] = useState(false);
    const [showApplySuccessModal, setShowApplySuccessModal] = useState(false);
    const [showRecycleTipsModal, setShowRecycleTipsModal] = useState(false);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [afsServiceId, setAfsServiceId] = useState('');
    const [curWareInfo, setCurWareInfo] = useState<DetailWareInfoListVo | null>(null);

    // 加入购物车
    const addCartHandler = (
      event: React.MouseEvent<HTMLButtonElement>,
      item: DetailWareInfoListVo,
    ) => {
      event.stopPropagation();
      // 埋点
      sendClickTrack.OrderDetail_AddtoCart({
        ...commonTrackParams,
        skuid: item.wareId || '',
      });
      const addonProducts =
        item?.serviceProductList?.map((ele) => ({
          skuId: ele.skuId,
          num: ele.buyCount || 1, // 服务品数量默认为1
          itemType: ITEM_TYPE.BigItem,
        })) || undefined;
      addCartUtil([{ skuId: item.wareId, num: '1', addonProducts }], true, true).then((res) => {
        if (res?.isDefinedToast) {
          // 商品缺货提醒展示
          setShowNotifyModal(true);
        }
      });
    };

    // 跳转商详
    const jumpProductDetail = (item: DetailWareInfoListVo) => {
      if (vipOrder) {
        // 会员订单点击不跳转
        return;
      }
      jumpToDp(item?.wareId || '', item?.jumpParam);
    };

    const returnItemHandler = (event: React.MouseEvent<HTMLButtonElement>, val: string) => {
      event.stopPropagation();
      // 埋点
      sendClickTrack.OrderDetail_RefundItem({
        ...commonTrackParams,
        skuid: val,
      });
      setCurSkuId(val);
      setShowReturnProductModal(true);
    };

    if (!wareInfoDTOList?.length) {
      return null;
    }

    // 计算Order items
    const calcNum = (list: DetailWareInfoListVo[]) => {
      return list.reduce((sum: number, item) => {
        return sum + Number(item.buyCount) + (item.serviceProductList?.length || 0);
      }, 0);
    };

    // view Detail 按钮点击刷新订详，关闭弹框
    const refreshDetailPage = () => {
      setShowApplySuccessModal(false);
      navigate(`/my-orders/refund?afsServiceId=${afsServiceId}&orderId=${orderId}`);
    };

    const orderServiceAgreementFloor = (productItem: any) => {
      return (
        <div className={styles.serviceAgreementWrap}>
          <OrderServiceAgreement
            serviceData={productItem?.serviceProductList}
            mainSkuId={productItem?.wareId}
            currencySymbol={localInfo?.currencySymbol}
          />
        </div>
      );
    };

    return (
      <div className={styles.wrap}>
        <div className={styles.productTitleWrap}>
          <div className={styles.product_title}>
            {t('order.orderDetail.orderDetailProduct.orderItemsText')}
            {wareInfoDTOList?.length ? `(${calcNum(wareInfoDTOList)})` : null}
          </div>
          {/* 大件回收标显示 */}
          {orderTagContentVO && orderTagContentVO?.tagSet?.includes('recycle') && (
            <div
              className={styles.recycleWrap}
              onClick={() => {
                setShowRecycleTipsModal(true);
              }}
            >
              {orderTagContentVO?.tagMainContentMap?.recycle?.title}
              <TipIcon className={styles.tipIco} width={14} height={14} />
            </div>
          )}
        </div>

        <ul className={styles.product_outer_wrap}>
          {wareInfoDTOList?.length
            ? wareInfoDTOList.map((item, index) => {
                const showOriginPrice =
                  item.crossedUnitPrice && Number(item.crossedUnitPrice) > Number(item.jdPriceShow);
                return (
                  <>
                    <li
                      key={index}
                      className={styles.productWrap}
                      onClick={() => jumpProductDetail(item)}
                    >
                      <div
                        className={cn(styles.productContentWrap, {
                          [styles.productSpecialWrap]: item.addCartShow || item.repairState === '1',
                          [styles.disabledClickStyle]: vipOrder,
                        })}
                      >
                        <div className={styles.left_product}>
                          <div className={styles.product_info}>
                            <div className={styles.product_img}>
                              <CDNImage
                                src={item.imageShortUrl || ''}
                                width={60}
                                height={60}
                                style={{ objectFit: 'contain', height: '60px' }}
                              />
                            </div>
                            <div
                              className={cn(styles.product_main_info, {
                                [styles.nlProductMainInfo]:
                                  langCookie === 'nl_NL' || langCookie === 'fr_FR',
                                [styles.deProductMainInfo]: langCookie === 'de_DE',
                              })}
                            >
                              <div className={styles.productName}>
                                <div className={styles.tagsWrap}>
                                  <ProductTag tagList={item?.showTagList || []} />
                                </div>
                                <div className={styles.product_name_cont}>{item.wareName}</div>
                              </div>
                              <div className={styles.product_desc}>{item.propertyExt}</div>
                            </div>
                          </div>
                        </div>
                        <div className={styles.right_product}>
                          <div className={styles.price_wrap}>
                            {/* 到手价 */}
                            <div className={styles.real_price}>
                              <JDIPrice
                                symbol={localInfo?.currencySymbol || ''}
                                price={item.jdPriceShow || ''}
                                uiType={1}
                                containerStyle={{
                                  fontSize: '12px',
                                  lineHeight: '16px',
                                  color: item.showStyle === 2 ? '#7014CC' : '#1A1A1A',
                                }}
                                integerStyle={{
                                  fontSize: '18px',
                                  lineHeight: '24px',
                                  height: '24px',
                                }}
                              />
                            </div>
                            {/* 划线价 */}
                            {/* 划线价与京东价一致则不展示 */}
                            {showOriginPrice ? (
                              <div className={styles.origin_price}>
                                <JDIPrice
                                  symbol={localInfo?.currencySymbol || ''}
                                  price={item.crossedUnitPrice || ''}
                                  uiType={2}
                                  containerStyle={{
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                  }}
                                  lineThrough
                                />
                              </div>
                            ) : null}
                          </div>
                          <div className={styles.product_num}>×{item.buyCount}</div>
                          {/* 操作按钮栏 */}
                          <div
                            className={cn(styles.btn_group, {
                              [styles.nlBtnGroup]: langCookie === 'nl_NL' || langCookie === 'fr_FR',
                              [styles.deBtnGroup]: langCookie === 'de_DE',
                            })}
                          >
                            {item.repairState === '1' ? (
                              <CommonButton
                                size='small'
                                variant='outlined'
                                onClick={(e) => {
                                  returnItemHandler(e, item?.wareId || '');
                                }}
                                style={{
                                  marginRight: '8px',
                                }}
                                exptag={'exp|' + EXPOSURE_RECORD.OrderDetail_RefundItemExpo}
                                data-exptag-config={JSON.stringify({
                                  useClick: false,
                                  stay_duration: 0.3,
                                  repeated: false,
                                  area_rate: 0.5,
                                })}
                                data-exptag-json-param={JSON.stringify({
                                  ...commonTrackParams,
                                  skuid: item?.wareId || '',
                                })}
                              >
                                {item.repairTitle}
                              </CommonButton>
                            ) : null}
                            {item.addCartShow && (
                              <CommonButton
                                size='small'
                                variant='outlined'
                                onClick={(e) => {
                                  setCurWareInfo(item);
                                  addCartHandler(e, item);
                                }}
                                style={{
                                  borderColor: '#cc0c1c',
                                  color: '#cc0c1c',
                                }}
                                exptag={'exp|' + EXPOSURE_RECORD.OrderDetail_AddtoCartExpo}
                                data-exptag-config={JSON.stringify({
                                  useClick: false,
                                  stay_duration: 0.3,
                                  repeated: false,
                                  area_rate: 0.5,
                                })}
                                data-exptag-json-param={JSON.stringify({
                                  ...commonTrackParams,
                                  skuid: item?.wareId || '',
                                })}
                              >
                                {t('order.orderDetail.orderDetailProduct.addCartBtnText')}
                              </CommonButton>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* 服务楼层 */}
                      {!!item?.serviceProductList?.length && orderServiceAgreementFloor(item)}
                    </li>
                  </>
                );
              })
            : null}
        </ul>
        {/* 售后商品选择弹窗 */}
        {showReturnProductModal && (
          <ReturnProductModal
            isOpen={showReturnProductModal}
            closeHandler={() => setShowReturnProductModal(false)}
            handleConfirmClick={() => {
              setShowReturnProductModal(false);
              setShowReturnRequestModal(true);
            }}
            orderId={orderId}
            skuId={Number(curSkuId)} // 将后端返回的字符串类型转化为数字
            skuList={wareInfoDTOList.map((item) => {
              return {
                skuId: item.wareId || '',
                skuUUid: item.skuUuid || '',
              };
            })}
          />
        )}
        {/* 售后申请表单弹窗 */}
        {showReturnRequestModal && (
          <ReturnRequestModal
            isOpen={showReturnRequestModal}
            closeHandler={() => setShowReturnRequestModal(false)}
            handleConfirmClick={(val) => {
              setShowReturnRequestModal(false);
              setShowApplySuccessModal(true);
              setAfsServiceId(val);
            }}
            orderId={orderId}
            postCode={postCode}
            skuIds={wareInfoDTOList.map((item) => item.wareId).join(',')}
          />
        )}

        {showApplySuccessModal && (
          <ApplySuccessModal
            isOpen={showApplySuccessModal}
            closeHandler={() => setShowApplySuccessModal(false)}
            viewDetailHandler={refreshDetailPage}
          />
        )}

        {/* 大件回收提示弹框 */}
        {showRecycleTipsModal && (
          <AgeTipsModal
            isOpen={showRecycleTipsModal}
            closeHandler={() => setShowRecycleTipsModal(false)}
            // @ts-ignore
            modalInfo={orderTagContentVO?.tagMainContentMap?.recycle}
          />
        )}
        {/* 缺货提醒弹框 */}
        <Dialog
          sx={{
            '& .MuiDialog-paper': {
              width: '480px', // 设置自定义宽度
              minWidth: 'auto', // 取消最大宽度限制
            },
          }}
          open={showNotifyModal}
          onClose={() => setShowNotifyModal(false)}
        >
          {/* 已订阅和未订阅提醒展示 */}
          <DialogContent>
            {curWareInfo?.subscribeStatus
              ? t('cart.page.notifiedTitle')
              : t('order.orderDetail.orderDetailProduct.singleNotifyTitle')}
          </DialogContent>
          {/* Button */}
          <DialogActions>
            {curWareInfo?.subscribeStatus ? ( // 已订阅提醒展示
              <CommonButton variant='outlined' onClick={() => setShowNotifyModal(false)}>
                {t('common.btnText.ok')}
              </CommonButton>
            ) : (
              <>
                <CommonButton variant='outlined' onClick={() => setShowNotifyModal(false)}>
                  {t('common.btnText.cancel')}
                </CommonButton>
                <CommonButton
                  variant='contained'
                  sx={{ boxShadow: 'none' }}
                  onClick={() => {
                    setShowNotifyModal(false);
                    OutOfStackController.getInstance().openOutOfStockDialog(
                      [curWareInfo?.wareId || ''],
                      () => {
                        onRefresh && onRefresh();
                      },
                    );
                  }}
                >
                  {t('cart.page.toNotify')}
                </CommonButton>
              </>
            )}
          </DialogActions>
        </Dialog>
      </div>
    );
  },
);

export default orderDetailProduct;
